/**
 * How a verdict signal looks, and how many of them to show at once.
 *
 * Pure and dependency-free so CRA's jest can test it — the same reason
 * `tierTone.ts` and `watchFeed.ts` are their own modules.
 *
 * THE COLOUR RULE IS THE SAME ONE `tierTone` FOLLOWS, and for the same reason.
 * Most of what a verdict says on a healthy watch is a NOTE — "nothing has
 * changed", "two kinds are muted here" — and a panel that painted every note
 * would be a wall of colour with the one real blocker lost inside it. So notes
 * are plain text, and only a blocker is red.
 *
 * AND A VERDICT IS NOT A LIST OF EVERYTHING WRONG. Five signals at once is a
 * to-do list nobody starts. `visible` keeps the worst few and says how many
 * were held back, which is the same "and N more" discipline the digest uses.
 */
import type { WatchVerdictSeverity, WatchVerdictSignal } from '../../types';

export interface VerdictTone {
  /** Tailwind classes for the chip. */
  chip: string;
  /** Tailwind classes for the row's left border. */
  edge: string;
  /** What the chip says. One word, because it is read at a glance. */
  label: string;
  /** Short reason, for a title attribute. */
  hint: string;
}

const TONES: Record<WatchVerdictSeverity, VerdictTone> = {
  blocker: {
    chip:
      'bg-red-100 text-red-800 border border-red-200 ' +
      'dark:bg-red-900/40 dark:text-red-200 dark:border-red-800',
    edge: 'border-l-2 border-red-400 dark:border-red-700',
    label: 'Stopped',
    hint: 'This watch is not reporting - its silence does not mean nothing changed',
  },
  warn: {
    chip:
      'bg-amber-100 text-amber-800 border border-amber-200 ' +
      'dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800',
    edge: 'border-l-2 border-amber-400 dark:border-amber-700',
    label: 'Tune',
    hint: 'Working, but the settings are letting through more or less than you want',
  },
  note: {
    chip: 'text-gray-500 dark:text-gray-400',
    edge: 'border-l-2 border-gray-200 dark:border-gray-700',
    label: 'FYI',
    hint: 'Nothing to fix - worth knowing so the quiet is not mistaken for a fault',
  },
};

/** A severity this client has not been taught yet must not shout. */
export function verdictTone(severity: string): VerdictTone {
  return TONES[severity as WatchVerdictSeverity] ?? TONES.note;
}

/** How many signals to show before folding the rest away. */
export const MAX_SHOWN = 3;

export interface Shown {
  signals: WatchVerdictSignal[];
  /** How many were held back. 0 when everything is shown. */
  more: number;
}

/**
 * The worst few signals, and how many were held back.
 *
 * The server already orders worst-first, so this takes from the front rather
 * than re-sorting: re-deriving the order here is how a client ends up
 * disagreeing with the email about which problem matters most.
 */
export function visible(
  signals: WatchVerdictSignal[],
  limit: number = MAX_SHOWN,
): Shown {
  const all = signals ?? [];
  if (all.length <= limit) return { signals: all, more: 0 };
  return { signals: all.slice(0, limit), more: all.length - limit };
}

/**
 * The one line to put on a collapsed panel.
 *
 * Leads with the worst signal's own label rather than a count, because "Every
 * check is switched off" is the thing to read and "3 findings" is not.
 */
export function headline(verdict: {
  worst: string;
  signals: WatchVerdictSignal[];
} | null): string {
  const signals = verdict?.signals ?? [];
  if (!signals.length) return 'Nothing to report about these settings.';
  const first = signals[0];
  const rest = signals.length - 1;
  return rest > 0 ? `${first.label} · and ${rest} more` : first.label;
}
